import { encryptForUserWithFallback, decryptForUser } from "@/lib/encryption";
import type { UserPreferencesRow } from "@/services/preferencesService";

export interface PanDetailsFields {
  holderName: string;
  dateOfBirth: string;
  fatherName: string;
  address: string;
}

export interface LoadedPanState {
  maskedPan: string | null;
  details: PanDetailsFields;
  hasSavedPan: boolean;
}

const EMPTY_DETAILS: PanDetailsFields = {
  holderName: "",
  dateOfBirth: "",
  fatherName: "",
  address: "",
};

/** SHA-256 hex digest for dedup/lookup (stored in `pan_hash` column). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function maskPanNumber(pan: string): string {
  const clean = pan.replace(/\s+/g, "").toUpperCase();
  const digits = clean.match(/\d/g);
  const lastFour = digits && digits.length >= 4 ? digits.slice(-4).join("") : clean.slice(-4);
  return `XXXXX${lastFour}`;
}

export async function encryptPanDetailsFields(
  userId: string,
  details: PanDetailsFields
): Promise<{ cipher: string; iv: string } | null> {
  const payload = JSON.stringify(details);
  const result = await encryptForUserWithFallback(payload, userId);
  if (!result.encrypted || !result.iv) {
    return null;
  }
  return { cipher: result.cipher, iv: result.iv };
}

function parsePanDetailsRecord(record: Record<string, unknown>): PanDetailsFields {
  return {
    holderName: typeof record.holderName === "string" ? record.holderName : "",
    dateOfBirth: typeof record.dateOfBirth === "string" ? record.dateOfBirth : "",
    fatherName: typeof record.fatherName === "string" ? record.fatherName : "",
    address: typeof record.address === "string" ? record.address : "",
  };
}

/**
 * Decrypt Stored Pan Details.
 */
async function decryptStoredPanDetails(
  userId: string,
  encrypted: string,
  iv: string
): Promise<PanDetailsFields | null> {
  try {
    const decrypted = await decryptForUser(encrypted, iv, userId);
    if (!decrypted) {
      return null;
    }
    const parsed = JSON.parse(decrypted) as Partial<PanDetailsFields>;
    return parsePanDetailsRecord(parsed as Record<string, unknown>);
  } catch {
    return null;
  }
}

/**
 * Decrypt Pan Details Fields.
 */
export async function decryptPanDetailsFields(
  userId: string,
  privacySettings: Record<string, unknown> | undefined
): Promise<PanDetailsFields> {
  if (!privacySettings || typeof privacySettings !== "object") {
    return { ...EMPTY_DETAILS };
  }

  const encrypted = privacySettings.encrypted_pan_details;
  const iv = privacySettings.pan_details_iv;

  if (typeof encrypted === "string" && typeof iv === "string" && encrypted.length > 0) {
    const decryptedDetails = await decryptStoredPanDetails(userId, encrypted, iv);
    if (decryptedDetails) {
      return decryptedDetails;
    }
  }

  const legacy = privacySettings.pan_details;
  if (legacy && typeof legacy === "object") {
    return parsePanDetailsRecord(legacy as Record<string, unknown>);
  }

  return { ...EMPTY_DETAILS };
}

export function getMaskedPanFromRow(row: UserPreferencesRow | null | undefined): string | null {
  if (!row) return null;
  const privacy = row.privacy_settings;
  if (privacy && typeof privacy === "object" && typeof privacy.masked_pan === "string") {
    return privacy.masked_pan;
  }
  if (typeof row.pan_hash === "string" && row.pan_hash.startsWith("XXXXX")) {
    return row.pan_hash;
  }
  return null;
}

/**
 * Load Pan State From Row.
 */
export async function loadPanStateFromRow(
  userId: string,
  row: UserPreferencesRow | null | undefined
): Promise<LoadedPanState> {
  const maskedPan = getMaskedPanFromRow(row);
  const hasSavedPan = Boolean(row?.encrypted_pan && maskedPan);
  const details = await decryptPanDetailsFields(userId, row?.privacy_settings);

  return {
    maskedPan,
    details,
    hasSavedPan,
  };
}

export const CLEARED_PAN_PRIVACY_SETTINGS = {
  encrypted_pan_details: null,
  pan_details_iv: null,
  masked_pan: null,
  pan_details: null,
} as const;
